import { produce } from 'immer';
import { useEffect, useRef, useState } from 'react';
import { SelectedLegendItemRecordState, SelectedLegendItemState } from '@/components';
import { TimeSeriesDataset } from '@/core';
import { TimeSeriesGroup } from './dataset';

export interface UseSelectedLegendItemsProps {
  timeChartData: TimeSeriesDataset[];

  /**
   * When specified, the chart will attempt to initialize with specified item focused in the
   * legend on intialization.
   */
  focusedItemId?: string;

  timeSeriesGrouping?: TimeSeriesGroup;
  isGrouped?: boolean;
}

export interface UseSelectedLegendItemsResponse {
  selectedLegendItems: SelectedLegendItemState;
  setSelectedLegendItems: React.Dispatch<React.SetStateAction<SelectedLegendItemState>>;
}

/**
 * Returns `true` if all legend items are selected via the "ALL" value or a record with all values
 * set to true.
 */
function areAllLegendItemsSelected(selectedLegendItems: SelectedLegendItemState): boolean {
  if (selectedLegendItems === 'ALL') {
    // All items are definitely selected when set to the hardcoded "ALL"
    return true;
  }

  // All items might be selected when using a record, so we must iterate the values to check.
  const itemValues = Object.values(selectedLegendItems);
  for (let i = 0; i < itemValues.length; i++) {
    if (!itemValues[i]) {
      // Return at the first falsy value because we know not all are selected.
      return false;
    }
  }

  // If we haven't returned at this point, all items values were true, so all items were selected.
  return true;
}

function updateSelectedLegendItemsWithNewData(
  prevSelectedLegendItems: SelectedLegendItemState,
  dataSource: TimeSeriesDataset[] | TimeSeriesGroup
): SelectedLegendItemState {
  if (prevSelectedLegendItems === 'ALL') {
    // Keep set to "ALL" because it automatically includes all items.
    return prevSelectedLegendItems;
  }

  const newSelectedLegendItems = produce(prevSelectedLegendItems, (draftSelectedLegendItems) => {
    const iterableData = Array.isArray(dataSource) ? dataSource : Object.keys(dataSource);

    // Keep track of all the keys currently in the selectedLegendItems key-value store.
    const selectedLegendItemsSet = new Set<string>(Object.keys(draftSelectedLegendItems));
    const allPrevItemsSelected = areAllLegendItemsSelected(draftSelectedLegendItems);

    // Iterate time chart data and add any new items missing from the
    // prevSelectedLegendItems key-value store.
    for (let i = 0; i < iterableData.length; i++) {
      const item = iterableData[i];

      const seriesId = typeof item === 'string' ? item : item?.id;
      if (!seriesId) {
        continue;
      }

      // Remove series names from the chart data as we see them, so that at the
      // end, we known which keys are no longer used and can be removed.
      selectedLegendItemsSet.delete(`${seriesId}`);

      if (draftSelectedLegendItems[seriesId] === undefined) {
        // if a series in the new data is not present in the previously selected legend items:
        // - Set it to `true` if all items were selected previously, allowing the user to continue
        //   viewing all results.
        // - Set it to `false` if only some items were selected previously, allowing the user to
        //   retain their previous limited selection.
        draftSelectedLegendItems[seriesId] = allPrevItemsSelected;
      }
    }

    // Keys remaining in the set do not exist in the new time chart data. Remove them to avoid
    // the key-value store growing forever as data updates.
    if (selectedLegendItemsSet.size > 0) {
      for (const key of selectedLegendItemsSet) {
        delete draftSelectedLegendItems[key];
      }
    }
  });

  return newSelectedLegendItems;
}

function getSingleLegendItemSelectedState(
  timeChartData: TimeSeriesDataset[],
  focusedItemId: string
): SelectedLegendItemState {
  const newSelectedLegendItems: SelectedLegendItemRecordState = {};

  for (let i = 0; i < timeChartData.length; i++) {
    const seriesId = timeChartData[i]?.id;
    if (!seriesId) {
      continue;
    }

    newSelectedLegendItems[seriesId] = seriesId === focusedItemId;
  }

  return newSelectedLegendItems;
}

/**
 * Hook that manages the selected legend items state. Includes processing to update the legend
 * items as needed when data changes.
 */
export const useSelectedLegendItems = ({
  timeChartData,
  focusedItemId,
  isGrouped,
  timeSeriesGrouping,
}: UseSelectedLegendItemsProps): UseSelectedLegendItemsResponse => {
  const [selectedLegendItems, setSelectedLegendItems] = useState<SelectedLegendItemState>('ALL');
  const isFocusedItemInitialized = useRef<boolean>(false);
  const prevIsGrouped = useRef<boolean>(!!isGrouped);

  const selectByGroup = !!isGrouped && !!timeSeriesGrouping;

  useEffect(() => {
    if (isGrouped !== prevIsGrouped.current) {
      // Swap back to "ALL" when grouping changes because it's hard to swap back-and-forth and keep
      // state. We may attempt to implement a more complex handoff between the grouped and not-grouped
      // selection in the future, but this is the intended MVP behavior.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedLegendItems('ALL');
      prevIsGrouped.current = !!isGrouped;
    }
  }, [isGrouped]);

  // Update selected legend items when not grouped.
  useEffect(() => {
    if (selectByGroup) {
      // timeChartData is not used to update the selected legend items when grouped.
      return;
    }

    // Update legend items.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedLegendItems((prevSelectedLegendItems) => {
      return updateSelectedLegendItemsWithNewData(prevSelectedLegendItems, timeChartData);
    });
  }, [selectByGroup, timeChartData]);

  // Update selected legend items when grouped.
  useEffect(() => {
    if (!selectByGroup) {
      // Only use the time series grouping when grouped and grouping data is available.
      return;
    }
    // Update legend items.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedLegendItems((prevSelectedLegendItems) => {
      return updateSelectedLegendItemsWithNewData(prevSelectedLegendItems, timeSeriesGrouping);
    });
  }, [selectByGroup, timeSeriesGrouping]);

  useEffect(() => {
    // If focused item is specified, adjust the legend to focus that item when we have
    // data. We only do this once to avoid overriding the user's legend interactions if the
    // data is refreshed.
    if (!isFocusedItemInitialized.current && focusedItemId && timeChartData.length) {
      isFocusedItemInitialized.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedLegendItems(getSingleLegendItemSelectedState(timeChartData, focusedItemId));
    } else if (isFocusedItemInitialized.current && !focusedItemId) {
      // If the focused item has been initialized and then was set to undefined, reset selected
      // items to 'ALL'.
      setSelectedLegendItems('ALL');
    }
  }, [timeChartData, focusedItemId]);

  return {
    selectedLegendItems,
    setSelectedLegendItems,
  };
};
